import { create } from 'zustand';
import { TextTagData } from '../dto/EntityRepository';
import type { ResourceScope, ResourceStatus } from '../util/scopedResource';

/** TextTag 백엔드 정렬과 동일: 이름(ko) 사전순 → index → id */
function sortTagsLikeServer(list: TextTagData[]): TextTagData[] {
  return [...list].sort((a, b) => {
    const nameCmp = a.name.localeCompare(b.name, 'ko-KR');
    if (nameCmp !== 0) return nameCmp;
    if (a.index !== b.index) return a.index - b.index;
    return a.id - b.id;
  });
}

const isSameTagList = (prev: TextTagData[], next: TextTagData[]): boolean =>
  prev.length === next.length &&
  prev.every((tag, index) => {
    const other = next[index];
    return tag.id === other.id && tag.name === other.name && tag.index === other.index;
  });

interface TextTagState {
  tags: TextTagData[];
  selectedTags: TextTagData[];
  /** 라벨없음. 다른 라벨 선택과 동시에 켜지지 않는다. */
  untaggedSelected: boolean;
  listOwnerAui: string | null;
  listScope: ResourceScope | null;
  status: ResourceStatus;
  beginTextTagListFetch: (ownerAui: string, scope: ResourceScope) => void;
  setTags: (
    tags: TextTagData[],
    ownerAui: string,
    scope: ResourceScope,
  ) => void;
  setTextTagListError: () => void;
  addSelectedTag: (tag: TextTagData) => void;
  removeSelectedTag: (tag: TextTagData) => void;
  selectUntagged: () => void;
  clearUntagged: () => void;
  clearSelectedTags: () => void;
  afterCreateTag: (tag: TextTagData) => void;
  afterUpdateTag: (tag: TextTagData) => void;
  afterDeleteTag: (tagId: number) => void;
  clearTags: () => void;
}

export const useTextTagStore = create<TextTagState>((set) => ({
  tags: [],
  selectedTags: [],
  untaggedSelected: false,
  listOwnerAui: null,
  listScope: null,
  status: 'idle',
  beginTextTagListFetch: (ownerAui, scope) => set((state) => {
    // 같은 owner+scope 재fetch: success 유지(SWR). 다르면 loading으로 숨김.
    if (
      state.listOwnerAui === ownerAui &&
      state.listScope === scope &&
      state.status === 'success'
    ) {
      return state;
    }
    return { status: 'loading' };
  }),
  setTags: (tags, ownerAui, scope) => set((state) => {
    const sorted = sortTagsLikeServer(tags);
    const nextSelected = state.selectedTags.filter((selected) =>
      sorted.some((tag) => tag.id === selected.id),
    );
    const nextUntagged = sorted.length === 0 ? false : state.untaggedSelected;
    if (
      state.status === 'success' &&
      state.listOwnerAui === ownerAui &&
      state.listScope === scope &&
      isSameTagList(state.tags, sorted) &&
      isSameTagList(state.selectedTags, nextSelected) &&
      state.untaggedSelected === nextUntagged
    ) {
      return state;
    }
    return {
      tags: sorted,
      selectedTags: nextSelected,
      untaggedSelected: nextUntagged,
      listOwnerAui: ownerAui,
      listScope: scope,
      status: 'success',
    };
  }),
  setTextTagListError: () => set({ status: 'error' }),
  addSelectedTag: (tag) => set((state) => {
    const already = state.selectedTags.some((selected) => selected.id === tag.id);
    if (already && !state.untaggedSelected) return state;
    return {
      untaggedSelected: false,
      selectedTags: already ? state.selectedTags : [...state.selectedTags, tag],
    };
  }),
  removeSelectedTag: (tag) => set(({ selectedTags }) => ({
    selectedTags: selectedTags.filter((t) => t.id !== tag.id),
  })),
  selectUntagged: () => set({ selectedTags: [], untaggedSelected: true }),
  clearUntagged: () => set({ untaggedSelected: false }),
  clearSelectedTags: () => set({ selectedTags: [], untaggedSelected: false }),
  afterCreateTag: (tag) =>
    set(({ tags }) => ({
      tags: sortTagsLikeServer([...tags, tag]),
    })),
  afterUpdateTag: (tag) =>
    set(({ tags, selectedTags }) => ({
      tags: sortTagsLikeServer(tags.map((t) => (t.id === tag.id ? tag : t))),
      selectedTags: selectedTags.map((selected) =>
        selected.id === tag.id ? tag : selected
      ),
    })),
  afterDeleteTag: (tagId) =>
    set(({ tags, selectedTags, untaggedSelected }) => {
      const nextTags = tags.filter((t) => t.id !== tagId);
      return {
        tags: nextTags,
        selectedTags: selectedTags.filter((tag) => tag.id !== tagId),
        untaggedSelected: nextTags.length === 0 ? false : untaggedSelected,
      };
    }),
  clearTags: () => set((state) => {
    if (
      state.tags.length === 0 &&
      state.selectedTags.length === 0 &&
      !state.untaggedSelected &&
      state.listOwnerAui === null &&
      state.listScope === null &&
      state.status === 'idle'
    ) {
      return state;
    }
    return {
      tags: [],
      selectedTags: [],
      untaggedSelected: false,
      listOwnerAui: null,
      listScope: null,
      status: 'idle',
    };
  }),
}));
